import { useEffect, useRef, useState } from "react";
import api from "../api/axiosInstance";
import { findDraftLanguage } from "../utils/overrideDraftCheck";

const FIT_STYLES = {
  GOOD: { bg: "#e6f4ea", fg: "#1e7e34", label: "Good fit" },
  WEAK: { bg: "#fff8e1", fg: "#8a6d00", label: "Weak fit — check carefully" },
  NONE: { bg: "#fdecea", fg: "#b3261e", label: "No role supports this" },
};

const STATUS_STYLES = {
  MISSING: { bg: "#f4f5f7", fg: "#5a5a5a" },
  SKILLS_ONLY_MATCH: { bg: "#fdf1e6", fg: "#b56a1f" },
};

// Same minimum the backend enforces in updateSkillSelection.
const MIN_JUSTIFICATION_LENGTH = 15;

// "Good", "good", " GOOD " all become "GOOD". Unknown values are shown as
// they are instead of silently turning into "Weak fit".
function getFitStyle(rawFit) {
  const key = String(rawFit || "").trim().toUpperCase();
  if (FIT_STYLES[key]) return { key, ...FIT_STYLES[key] };
  return { key, bg: "#f4f5f7", fg: "#5a5a5a", label: rawFit ? `Fit: ${rawFit}` : "Fit not rated" };
}

// "Software Engineer at Meta, USA (Jul 2025 – Present)"
function describeRole(role) {
  if (!role) return "";
  const title = role.title || "Role";
  const company = role.companyName || "(company)";
  return `${title} at ${company}${role.employmentDates ? ` (${role.employmentDates})` : ""}`;
}

// Field styles matching the app theme (blue #1a4d8f, 6px corners, gray labels).
// Prefixed "som-" so they can't clash with other styles in the app.
const MODAL_CSS = `
.som-label {
  display: block;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5a5a5a;
  margin-bottom: 6px;
}
.som-label-hint {
  text-transform: none;
  letter-spacing: 0;
  font-weight: normal;
  color: #8a8a8a;
}
.som-field {
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 13px;
  color: #1a1a1a;
  background-color: #fff;
  border: 1px solid #d0d5dc;
  border-radius: 6px;
  padding: 9px 12px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.som-field:hover {
  border-color: #a9b4c2;
}
.som-field:focus {
  border-color: #1a4d8f;
  box-shadow: 0 0 0 3px rgba(26, 77, 143, 0.15);
}
.som-field::placeholder {
  color: #9aa1a9;
}
.som-select {
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  padding-right: 36px;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%235a5a5a' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px 8px;
}
.som-textarea {
  resize: vertical;
  min-height: 96px;
  line-height: 1.5;
}
.som-checkbox {
  accent-color: #1a4d8f;
  width: 15px;
  height: 15px;
  margin-top: 1px;
  cursor: pointer;
}
.som-link-button {
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: #1a4d8f;
  text-decoration: underline;
  cursor: pointer;
}
.som-section-title {
  font-weight: bold;
  color: #1a1a1a;
}
.som-use-bullet {
  margin-top: 8px;
  font-size: 12px;
  font-weight: bold;
  color: #fff;
  background-color: #1a4d8f;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}
.som-use-bullet:hover {
  background-color: #153e73;
}
`;

// Popup for overriding a MISSING / SKILLS_ONLY_MATCH skill. Shows the AI
// suggestion (fit, how it could fit, a ready-to-use bullet for a specific
// role, risk, and questions to ask), a role picker, and a justification.
// "Use this bullet" fills the justification and role in one click; the
// recruiter then ticks the confirmation box and approves.
export default function SkillOverrideModal({ skill, skillType, optimizationId, onApprove, onClose }) {
  const [suggestion, setSuggestion] = useState(null);
  const [roles, setRoles] = useState([]);
  const [targetCompany, setTargetCompany] = useState("");
  const [justification, setJustification] = useState("");
  const [usedBullet, setUsedBullet] = useState(""); // AI bullet the recruiter chose, if any
  const [confirmed, setConfirmed] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState("");
  // Ignores a slow AI response that arrives after Regenerate or after closing.
  const requestSeq = useRef(0);

  const fetchSuggestion = async () => {
    const seq = ++requestSeq.current;
    setSuggesting(true);
    setSuggestError("");
    setSuggestion(null);
    try {
      const res = await api.post(`/optimization/${optimizationId}/suggest-skill-usage`, {
        skillType,
        name: skill.name,
        // Lets the backend write the bullet for the role the recruiter picked.
        targetCompany: targetCompany || undefined,
      });
      if (seq !== requestSeq.current) return;
      const s = res.data.suggestion;
      const roleList = res.data.roles || [];
      setSuggestion(s);
      setRoles(roleList);
      // Pre-select the AI's role only if the recruiter hasn't picked one yet.
      const suggestedRole = roleList.find((r) => r.index === s?.roleIndex);
      setTargetCompany((prev) => prev || suggestedRole?.companyName || "");
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setSuggestError(
        err.response?.data?.message || "The AI suggestion couldn't be loaded. You can still write the justification yourself."
      );
    } finally {
      if (seq === requestSeq.current) setSuggesting(false);
    }
  };

  useEffect(() => {
    fetchSuggestion();

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // Stop the page behind the popup from scrolling.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      requestSeq.current += 1;
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = justification.trim();
  const draftPhrases = findDraftLanguage(justification);
  const meetsMinimum = trimmed.length >= MIN_JUSTIFICATION_LENGTH;
  const canSubmit = meetsMinimum && draftPhrases.length === 0 && confirmed;

  const handleApprove = () => {
    if (!canSubmit) return;
    // Third argument is the AI bullet the recruiter accepted ("" if they wrote
    // their own justification). Parents that only read the first two are unaffected.
    onApprove(trimmed, targetCompany || null, usedBullet || null);
  };

  const statusStyle = STATUS_STYLES[skill.status] || STATUS_STYLES.MISSING;
  const fit = suggestion ? getFitStyle(suggestion.fit) : null;
  const isNoFit = fit?.key === "NONE";
  const questions = suggestion?.questionsToConfirm || [];

  // The role the AI wrote its bullet for.
  const bulletRole = suggestion ? roles.find((r) => r.index === suggestion.roleIndex) || null : null;
  // Recruiter switched to a different role after the bullet was written.
  const roleChanged = !!(bulletRole && targetCompany && bulletRole.companyName !== targetCompany);
  const bullet = !isNoFit ? suggestion?.sampleBullet || "" : "";

  // One click: fill the justification with the AI bullet and lock in its role.
  const useBullet = () => {
    if (!bullet) return;
    const company = bulletRole?.companyName || targetCompany || "their company";
    setJustification(`Confirmed with candidate — at ${company}: ${bullet}`);
    setUsedBullet(bullet);
    if (bulletRole?.companyName) setTargetCompany(bulletRole.companyName);
  };

  // Copies the AI's draft justification into the textarea as a starting point.
  // The draft-wording check still forces an edit before Approve is enabled.
  const useAiDraft = () => {
    if (suggestion?.justification) {
      setJustification(suggestion.justification);
      setUsedBullet("");
    }
  };

  // Typing over a used bullet means the recruiter is writing their own text.
  const onJustificationChange = (value) => {
    setJustification(value);
    if (usedBullet && !value.includes(usedBullet)) setUsedBullet("");
  };

  // Neutral example that fits any skill, using the selected role's company.
  const placeholder = `e.g. "Confirmed on a call: at ${targetCompany || "their company"}, they set this up and maintained it in production for the team's main services."`;

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        zIndex: 1000,
      }}
    >
      <style>{MODAL_CSS}</style>

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="skill-override-title"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          width: "100%",
          maxWidth: "680px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
        }}
      >
        {/* Header: which skill is being overridden, and why it needs it */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e0e0e0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
            <div>
              <div style={{ fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.04em", color: "#5a5a5a" }}>
                Override {skillType} skill
              </div>
              <h3 id="skill-override-title" style={{ margin: "4px 0 8px", fontSize: "17px", color: "#1a4d8f" }}>
                {skill.name}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{ background: "none", border: "none", fontSize: "20px", lineHeight: 1, cursor: "pointer", color: "#5a5a5a", padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <span
            style={{
              backgroundColor: statusStyle.bg,
              color: statusStyle.fg,
              padding: "3px 10px",
              borderRadius: "3px",
              fontSize: "11px",
              fontWeight: "bold",
            }}
          >
            {skill.status.replace(/_/g, " ")}
          </span>
          {skill.reason && <p style={{ fontSize: "13px", color: "#5a5a5a", margin: "8px 0 0" }}>{skill.reason}</p>}
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", overflowY: "auto", fontSize: "13px" }}>
          {suggesting && (
            <p style={{ color: "#5a5a5a", margin: "0 0 12px" }}>AI is checking the candidate's roles for where this skill fits...</p>
          )}
          {suggestError && <p style={{ color: "#b3261e", margin: "0 0 12px" }}>{suggestError}</p>}

          {suggestion && (
            <div
              style={{
                backgroundColor: "#f8f9fb",
                border: "1px solid #e3e6ea",
                borderRadius: "6px",
                padding: "12px 14px",
                marginBottom: "16px",
                lineHeight: "1.55",
              }}
            >
              <span
                style={{
                  backgroundColor: fit.bg,
                  color: fit.fg,
                  padding: "2px 8px",
                  borderRadius: "3px",
                  fontSize: "11px",
                  fontWeight: "bold",
                }}
              >
                AI suggestion · {fit.label}
              </span>

              {/* Ready-to-use bullet, shown first, labelled with its role */}
              {bullet && (
                <div
                  style={{
                    margin: "10px 0 0",
                    padding: "10px 12px",
                    backgroundColor: "#fff",
                    border: "1px solid #c9d3df",
                    borderLeft: "3px solid #1a4d8f",
                    borderRadius: "4px",
                  }}
                >
                  <div style={{ fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.04em", color: "#1a4d8f" }}>
                    Suggested bullet{bulletRole ? ` · ${describeRole(bulletRole)}` : ""}
                  </div>
                  <div style={{ marginTop: "4px", color: "#1a1a1a" }}>• {bullet}</div>
                  {usedBullet === bullet ? (
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "#1e7e34", fontWeight: "bold" }}>
                      ✓ Added to justification — tick the confirmation box and approve.
                    </div>
                  ) : (
                    <button type="button" className="som-use-bullet" onClick={useBullet}>
                      Use this bullet
                    </button>
                  )}
                  {roleChanged && (
                    <div style={{ marginTop: "6px", fontSize: "12px", color: "#8a6d00" }}>
                      You picked a different role. Click "Regenerate suggestion" to get a bullet written for that role.
                    </div>
                  )}
                </div>
              )}

              {suggestion.usage && (
                <p style={{ margin: "10px 0 0" }}>
                  <span className="som-section-title">{isNoFit ? "Why not: " : "Why it fits: "}</span>
                  {suggestion.usage}
                </p>
              )}

              {suggestion.caveat && (
                <p style={{ margin: "10px 0 0", color: "#8a6d00" }}>
                  <span className="som-section-title" style={{ color: "#8a6d00" }}>Check first: </span>
                  {suggestion.caveat}
                </p>
              )}

              {questions.length > 0 && (
                <details style={{ margin: "10px 0 0" }}>
                  <summary className="som-section-title" style={{ cursor: "pointer" }}>
                    Questions to ask the candidate ({questions.length})
                  </summary>
                  <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                    {questions.map((q, i) => (
                      <li key={i} style={{ marginBottom: "3px" }}>
                        {q}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {roles.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <label htmlFor="som-role" className="som-label">
                Add to role
              </label>
              <select
                id="som-role"
                className="som-field som-select"
                value={targetCompany}
                onChange={(e) => setTargetCompany(e.target.value)}
              >
                <option value="">Let AI pick the best-fitting role</option>
                {roles.map((r) => (
                  <option key={r.index} value={r.companyName || ""}>
                    {describeRole(r)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
              <label htmlFor="som-justification" className="som-label">
                Justification <span className="som-label-hint">— what the candidate confirmed</span>
              </label>
              {suggestion?.justification && (
                <button type="button" className="som-link-button" onClick={useAiDraft} style={{ marginBottom: "6px" }}>
                  Use AI draft as starting point
                </button>
              )}
            </div>
            <textarea
              id="som-justification"
              className="som-field som-textarea"
              rows={4}
              value={justification}
              onChange={(e) => onJustificationChange(e.target.value)}
              placeholder={placeholder}
            />
            <div
              style={{
                fontSize: "11px",
                textAlign: "right",
                color: meetsMinimum ? "#1e7e34" : "#8a8a8a",
                marginTop: "4px",
              }}
            >
              {meetsMinimum ? "✓ " : ""}
              {trimmed.length}/{MIN_JUSTIFICATION_LENGTH} characters minimum
            </div>
          </div>

          {draftPhrases.length > 0 && (
            <p style={{ color: "#b3261e", margin: "4px 0 0" }}>
              Remove draft wording before saving: {draftPhrases.join(", ")}. Edit it to say only what the candidate
              actually confirmed.
            </p>
          )}

          <label style={{ display: "flex", gap: "8px", alignItems: "flex-start", margin: "14px 0 0", cursor: "pointer" }}>
            <input type="checkbox" className="som-checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span>I've confirmed with the candidate that this is true</span>
          </label>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <button type="button" onClick={fetchSuggestion} disabled={suggesting} style={{ fontSize: "13px" }}>
            {suggesting ? "Suggesting..." : "Regenerate suggestion"}
          </button>
          <div style={{ display: "flex", gap: "8px" }}>
            <button type="button" className="btn-secondary" onClick={onClose} style={{ fontSize: "13px" }}>
              Cancel
            </button>
            <button type="button" onClick={handleApprove} disabled={!canSubmit} style={{ fontSize: "13px" }}>
              Approve with justification
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
